import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  ApplicationRoleGet,
  ApplicationRolePut,
} from "../../../../../models/ApplicationRole"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"
import { useApplicationRolePriorityFieldRenderer } from "../../../../../components/Helpers"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  fullDescription: z.string().optional(),
  isInternal: z.boolean(),
  isDefault: z.boolean(),
  isRequestable: z.boolean(),
  priority: z.number().min(0, "Priority is required"),
})

export function ApplicationRoleDetails(props: {
  readonly role: ApplicationRoleGet
  readonly applicationId: string
  readonly user: UserInfo
  readonly setRole: (updatedRole: ApplicationRoleGet) => void
}) {
  const { role, applicationId, user, setRole } = props

  const returnURL = `/applications/${applicationId}#roles`

  const formMethods = useForm<ApplicationRolePut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: role.name,
      description: role.description,
      fullDescription: role.fullDescription,
      isInternal: role.isInternal || false,
      isDefault: role.isDefault || false,
      isRequestable: role.isRequestable || false,
      priority: role.priority,
    })
  }, [role, reset])

  const onSubmit = useCallback(
    (values: ApplicationRolePut) =>
      digitalManagerApi
        .put<ApplicationRoleGet>(`/applications/${applicationId}/roles/${role.id}`, {
          applicationId,
          id: role.id,
          name: values.name,
          description: values.description,
          fullDescription: values.fullDescription,
          isInternal: values.isInternal,
          isDefault: values.isDefault,
          isRequestable: values.isRequestable,
          priority: values.priority,
        })
        .then((res) => {
          toast.success("Role updated")
          setRole(res.data)
        })
        .catch(() => {
          toast.error("Cannot update role")
          throw new Error("Cannot update role")
        }),
    [applicationId, role.id, setRole]
  )

  const handleTextChange = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRolePut,
        "name" | "description" | "fullDescription"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleBooleanToggle = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRolePut,
        "isInternal" | "isDefault" | "isRequestable"
      >
    ) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationRolePut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationRolePut, "description"> }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextChange]
  )

  const renderIsRequestableCheckbox = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRolePut, "isRequestable">
    }) => (
      <MUICheckBox
        {...field}
        id="isRequestable"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderFullDescriptionField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRolePut, "fullDescription">
    }) => (
      <MUITextField
        id="fullDescription"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.fullDescription?.message === "string"
            ? { fullDescription: { message: errors.fullDescription.message } }
            : undefined
        }
      />
    ),
    [errors.fullDescription, handleTextChange]
  )

  const renderIsInternalCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationRolePut, "isInternal"> }) => (
      <MUICheckBox
        {...field}
        id="isInternal"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsDefaultCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationRolePut, "isDefault"> }) => (
      <MUICheckBox
        {...field}
        id="isDefault"
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<ApplicationRolePut, "priority">) => (value: string) => {
      const parsedValue = value ? Number.parseInt(value, 10) : ""
      field.onChange(parsedValue)
    },
    []
  )

  const renderPriorityTextField = useApplicationRolePriorityFieldRenderer({
    handleParsedNumberChange,
    errors,
  })

  if (!role) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<ApplicationRolePut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{role.name}</div>,
        editItem: <Controller name="name" control={control} render={renderTextField} />,
      },
      {
        id: 1,
        label: "Description",
        displayItem: <div className="title">{role.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={control}
            render={renderDescriptionTextField}
          />
        ),
      },
      {
        id: 2,
        label: "Full Description",
        displayItem: <div className="title">{role.fullDescription}</div>,
        editItem: (
          <Controller
            name="fullDescription"
            control={control}
            render={renderFullDescriptionField}
          />
        ),
      },
      {
        id: 3,
        label: "Is Internal",
        displayItem: (
          <div className="title">{role.isInternal?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isInternal"
            control={control}
            render={renderIsInternalCheckbox}
          />
        ),
      },
      {
        id: 4,
        label: "Is Default",
        displayItem: <div className="title">{role.isDefault?.toString() || "false"}</div>,
        editItem: (
          <Controller
            name="isDefault"
            control={control}
            render={renderIsDefaultCheckbox}
          />
        ),
      },
      {
        id: 5,
        label: "Is Requestable",
        displayItem: (
          <div className="title">{role.isRequestable?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isRequestable"
            control={control}
            render={renderIsRequestableCheckbox}
          />
        ),
      },
      {
        id: 6,
        label: "Priority",
        displayItem: <div className="title">{role.priority}</div>,
        editItem: (
          <Controller
            name="priority"
            control={control}
            render={renderPriorityTextField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
